import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TriangleAlert } from "lucide-react";

import styles from "./CustomSuggestionsDropdown.module.scss";

const CustomSuggestionsDropdown = ({ title = "Sugerencias", suggestions = [], emptyText = "Sin sugerencias", noUnitText = "Sin unidad", onSelect }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 300 });
	const containerRef = useRef(null);
	const triggerRef = useRef(null);
	const dropdownRef = useRef(null);

	const updateDropdownPosition = () => {
		if (!triggerRef.current) {
			return;
		}

		const triggerRect = triggerRef.current.getBoundingClientRect();
		const measuredHeight = dropdownRef.current?.offsetHeight || 260;
		const dropdownWidth = 300;
		const viewportPadding = 8;
		const spaceBelow = globalThis.innerHeight - triggerRect.bottom;
		const spaceAbove = triggerRect.top;
		const shouldOpenUp = spaceBelow < measuredHeight + 16 && spaceAbove > spaceBelow;

		const top = shouldOpenUp
			? Math.max(viewportPadding, triggerRect.top - measuredHeight - 8)
			: Math.min(globalThis.innerHeight - measuredHeight - viewportPadding, triggerRect.bottom + 8);

		const left = Math.min(globalThis.innerWidth - dropdownWidth - viewportPadding, Math.max(viewportPadding, triggerRect.right - dropdownWidth));

		setDropdownPosition({ top, left, width: dropdownWidth });
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if ((containerRef.current && containerRef.current.contains(event.target)) || (dropdownRef.current && dropdownRef.current.contains(event.target))) {
				return;
			}
			setIsOpen(false);
		};

		document.addEventListener("click", handleClickOutside, true);
		return () => {
			document.removeEventListener("click", handleClickOutside, true);
		};
	}, []);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const rafId = globalThis.requestAnimationFrame(() => {
			updateDropdownPosition();
		});

		const handleViewportChange = () => {
			updateDropdownPosition();
		};

		globalThis.addEventListener("resize", handleViewportChange);
		globalThis.addEventListener("scroll", handleViewportChange, true);

		return () => {
			globalThis.cancelAnimationFrame(rafId);
			globalThis.removeEventListener("resize", handleViewportChange);
			globalThis.removeEventListener("scroll", handleViewportChange, true);
		};
	}, [isOpen, suggestions.length]);

	const hasSuggestions = suggestions.length > 0;

	return (
		<div className={styles.container} ref={containerRef}>
			<button ref={triggerRef} type="button" className={styles.trigger} onClick={() => setIsOpen(!isOpen)} title="Ver sugerencias">
				<TriangleAlert size={14} />
			</button>
			{isOpen &&
				createPortal(
					<div
						ref={dropdownRef}
						className={styles.dropdown}
						style={{
							position: "fixed",
							top: `${dropdownPosition.top}px`,
							left: `${dropdownPosition.left}px`,
							width: `${dropdownPosition.width}px`,
						}}
					>
						<div className={styles.title}>{title}</div>
						{hasSuggestions ? (
							suggestions.map((suggestion) => (
								<button
									type="button"
									className={styles.item}
									key={`${suggestion.value}-${suggestion.label}`}
									onClick={() => {
										setIsOpen(false);
										onSelect(suggestion);
									}}
								>
									<span className={styles.itemLabel}>{suggestion.label}</span>
									<span className={styles.itemMeta}>{suggestion.unit_measurement_label || noUnitText}</span>
								</button>
							))
						) : (
							<div className={styles.empty}>{emptyText}</div>
						)}
					</div>,
					document.body,
				)}
		</div>
	);
};

export default CustomSuggestionsDropdown;
